import { useState, useEffect } from 'react';
import { db, auth } from '../lib/firebase';
import { collection, query, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { TopNav } from '../components/TopNav';
import { scrapeWoltRestaurant } from '../lib/woltScraper';
import {
    Utensils, Plus, X, Edit2, Trash2, ChevronDown, ChevronUp,
    Link2, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import clsx from 'clsx';

export const HospitalityManager = () => {
    const { t, isRtl } = useLanguage();
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRestaurant, setEditingRestaurant] = useState(null);
    const [expandedItems, setExpandedItems] = useState({});

    // Form states
    const [name, setName] = useState('');
    const [menu, setMenu] = useState([]);

    // Wolt import states
    const [woltUrl, setWoltUrl] = useState('');
    const [scraping, setScraping] = useState(false);
    const [scrapeError, setScrapeError] = useState('');
    const [importPreview, setImportPreview] = useState(null); // { restaurantName, menu }

    const hostId = auth.currentUser?.uid;

    useEffect(() => {
        if (!hostId) return;
        const q = query(collection(db, `hosts/${hostId}/restaurants`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = [];
            snapshot.forEach(d => list.push({ id: d.id, ...d.data() }));
            setRestaurants(list);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [hostId]);

    const handleOpenModal = (res = null) => {
        if (res) {
            setEditingRestaurant(res);
            setName(res.name || '');
            setMenu((res.menu || []).map(item => ({
                ...item,
                modifiers: item.modifiers || [],
            })));
        } else {
            setEditingRestaurant(null);
            setName('');
            setMenu([]);
        }
        setWoltUrl('');
        setScrapeError('');
        setImportPreview(null);
        setExpandedItems({});
        setShowModal(true);
    };

    // ── Wolt Import ────────────────────────────────────────────────
    const handleWoltImport = async () => {
        if (!woltUrl.trim()) return;
        setScraping(true);
        setScrapeError('');
        setImportPreview(null);
        try {
            const result = await scrapeWoltRestaurant(woltUrl.trim());
            setImportPreview(result);
            // Pre-fill name from Wolt if not already editing a restaurant
            if (!editingRestaurant && !name.trim()) {
                setName(result.restaurantName);
            }
        } catch (err) {
            setScrapeError(err.message || 'שגיאה בייבוא מ-Wolt');
        } finally {
            setScraping(false);
        }
    };

    const applyImport = () => {
        if (!importPreview) return;
        if (!name.trim()) setName(importPreview.restaurantName);
        setMenu(importPreview.menu);
        setImportPreview(null);
        setWoltUrl('');
    };

    // ── Menu Editing ───────────────────────────────────────────────
    const handleAddMenuItem = () => {
        setMenu([...menu, { itemName: '', price: 0, description: '', modifiers: [] }]);
    };

    const handleRemoveMenuItem = (index) => {
        setMenu(menu.filter((_, i) => i !== index));
    };

    const handleMenuChange = (index, field, value) => {
        const updated = [...menu];
        updated[index] = { ...updated[index], [field]: value };
        setMenu(updated);
    };

    const toggleItemExpand = (index) => {
        setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
    };

    // ── Modifiers ─────────────────────────────────────────────────
    const addModifier = (itemIndex) => {
        const updated = [...menu];
        updated[itemIndex].modifiers = [
            ...(updated[itemIndex].modifiers || []),
            { name: '', required: false, multiSelect: false, options: [{ label: '', price: 0 }] },
        ];
        setMenu(updated);
    };

    const removeModifier = (itemIndex, modIndex) => {
        const updated = [...menu];
        updated[itemIndex].modifiers = updated[itemIndex].modifiers.filter((_, i) => i !== modIndex);
        setMenu(updated);
    };

    const updateModifier = (itemIndex, modIndex, field, value) => {
        const updated = [...menu];
        updated[itemIndex].modifiers[modIndex] = { ...updated[itemIndex].modifiers[modIndex], [field]: value };
        setMenu(updated);
    };

    const addModifierOption = (itemIndex, modIndex) => {
        const updated = [...menu];
        updated[itemIndex].modifiers[modIndex].options = [
            ...updated[itemIndex].modifiers[modIndex].options,
            { label: '', price: 0 },
        ];
        setMenu(updated);
    };

    const removeModifierOption = (itemIndex, modIndex, optIndex) => {
        const updated = [...menu];
        updated[itemIndex].modifiers[modIndex].options =
            updated[itemIndex].modifiers[modIndex].options.filter((_, i) => i !== optIndex);
        setMenu(updated);
    };

    const updateModifierOption = (itemIndex, modIndex, optIndex, field, value) => {
        const updated = [...menu];
        updated[itemIndex].modifiers[modIndex].options[optIndex] = {
            ...updated[itemIndex].modifiers[modIndex].options[optIndex],
            [field]: value,
        };
        setMenu(updated);
    };

    // ── Save ───────────────────────────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();
        if (!hostId || !name.trim()) return;

        const cleanMenu = menu.map(item => ({
            itemName: item.itemName.trim(),
            price: Number(item.price) || 0,
            description: item.description || '',
            modifiers: (item.modifiers || []).filter(m => m.name && m.options?.length > 0),
        })).filter(item => item.itemName);

        const data = { name: name.trim(), menu: cleanMenu, updatedAt: new Date().toISOString() };

        try {
            if (editingRestaurant) {
                await updateDoc(doc(db, `hosts/${hostId}/restaurants/${editingRestaurant.id}`), data);
            } else {
                await addDoc(collection(db, `hosts/${hostId}/restaurants`), {
                    ...data,
                    sessionsUsedCount: 0,
                    totalMealsOrdered: 0,
                    createdAt: new Date().toISOString(),
                });
            }
            setShowModal(false);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (resId) => {
        if (!hostId || !window.confirm(t('confirmDeleteRestaurant'))) return;
        await deleteDoc(doc(db, `hosts/${hostId}/restaurants/${resId}`));
    };

    // ── Render ─────────────────────────────────────────────────────
    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir={isRtl ? 'rtl' : 'ltr'}>
            <TopNav />
            <div className="flex flex-col flex-1 w-full max-w-xl mx-auto px-4 py-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-black text-purple-500 font-heebo">{t('hospitalityManagement')}</h1>
                        <p className="text-xs text-slate-500 mt-1 font-heebo">{t('hospitalitySub')}</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-black text-xs rounded-xl transition-colors font-heebo shadow-lg"
                    >
                        <Plus className="w-4 h-4" />
                        {t('addRestaurant')}
                    </button>
                </div>

                <div className="space-y-3">
                    {loading ? (
                        [1, 2].map(i => <div key={i} className="h-24 bg-slate-900 animate-pulse rounded-2xl" />)
                    ) : restaurants.length === 0 ? (
                        <div className="text-center py-20 text-slate-600">
                            <Utensils className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p className="font-heebo">{t('noRestaurants')}</p>
                            <p className="text-[10px] mt-2 font-heebo">{t('noRestaurantsSub')}</p>
                        </div>
                    ) : restaurants.map(res => (
                        <div key={res.id} className="bg-slate-900/60 border border-white/5 rounded-2xl p-4 hover:border-purple-500/20 transition-all">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-500">
                                        <Utensils className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white font-heebo">{res.name}</h3>
                                        <p className="text-[10px] text-slate-500 font-heebo">{t('menuCount', { count: res.menu?.length || 0 })}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => handleOpenModal(res)} className="p-2 text-slate-500 hover:text-purple-500 bg-slate-800 rounded-xl transition-colors">
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(res.id)} className="p-2 text-slate-500 hover:text-rose-500 bg-slate-800 rounded-xl transition-colors">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Modal ── */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
                    <div className="w-full sm:max-w-lg bg-slate-900 border border-white/10 rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92dvh]">

                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 flex-shrink-0">
                            <h3 className="text-base font-black text-white font-heebo">
                                {editingRestaurant ? t('editRestaurant') : t('addNewRestaurant')}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-500 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="flex flex-col flex-1 min-h-0 overflow-y-auto">
                            <div className="p-5 space-y-5">

                                {/* ── Wolt Import Card ── */}
                                <div className="bg-slate-800/60 border border-white/5 rounded-2xl p-4 space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-6 h-6 rounded-lg bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
                                            <Link2 className="w-3 h-3 text-sky-400" />
                                        </div>
                                        <span className="text-xs font-black text-slate-300 font-heebo">{t('importFromWolt')}</span>
                                        <span className="text-[10px] text-slate-600 font-heebo mr-auto">{t('optional')}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="url"
                                            value={woltUrl}
                                            onChange={e => setWoltUrl(e.target.value)}
                                            placeholder="https://wolt.com/he/isr/..."
                                            dir="ltr"
                                            className="flex-1 bg-slate-950/60 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white placeholder:text-slate-600 outline-none focus:border-sky-500/50 font-mono"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleWoltImport}
                                            disabled={scraping || !woltUrl.trim()}
                                            className="px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-xs font-black rounded-xl transition-colors flex items-center gap-1.5 flex-shrink-0"
                                        >
                                            {scraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                                            {scraping ? t('importing') : t('import')}
                                        </button>
                                    </div>

                                    {scrapeError && (
                                        <div className="flex items-start gap-2 text-rose-400 text-[11px] bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2">
                                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                            <span className="font-heebo">{scrapeError}</span>
                                        </div>
                                    )}

                                    {importPreview && (
                                        <div className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-3 space-y-2">
                                            <div className="flex items-center gap-2">
                                                <CheckCircle className="w-4 h-4 text-sky-400 flex-shrink-0" />
                                                <span className="text-xs text-sky-300 font-black font-heebo">{importPreview.restaurantName}</span>
                                                <span className="text-[10px] text-slate-500 mr-auto font-heebo">{t('menuCount', { count: importPreview.menu.length })}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                                                {importPreview.menu.slice(0, 12).map((item, i) => (
                                                    <span key={i} className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full font-heebo">
                                                        {item.itemName}
                                                    </span>
                                                ))}
                                                {importPreview.menu.length > 12 && (
                                                    <span className="text-[10px] text-slate-600 px-2 py-0.5 font-heebo">+{importPreview.menu.length - 12} נוספים</span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={applyImport}
                                                className="w-full py-2 bg-sky-600 hover:bg-sky-500 text-white text-xs font-black rounded-xl transition-colors font-heebo"
                                            >
                                                {t('applyImport')}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* ── Restaurant Name ── */}
                                <div>
                                    <label className="block text-xs text-slate-400 mb-1.5 font-heebo">{t('restaurantName')}</label>
                                    <input
                                        type="text" required value={name} onChange={e => setName(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:border-purple-500 outline-none font-heebo"
                                        placeholder={t('restaurantPlaceholder')}
                                    />
                                </div>

                                {/* ── Menu Items ── */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs text-slate-400 font-heebo">{t('menuCount', { count: menu.length })}</label>
                                        <button type="button" onClick={handleAddMenuItem} className="text-[10px] text-purple-500 font-bold hover:underline font-heebo">
                                            {t('addManually')}
                                        </button>
                                    </div>

                                    {menu.length === 0 && (
                                        <p className="text-center text-slate-600 text-[11px] py-4 font-heebo">{t('noRestaurantsSub')}</p>
                                    )}

                                    {menu.map((item, i) => (
                                        <div key={i} className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700/50">
                                            {/* Item Header */}
                                            <div className="flex items-center gap-2 p-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMenuItem(i)}
                                                    className="flex-shrink-0 w-5 h-5 bg-rose-500/20 hover:bg-rose-500 text-rose-400 hover:text-white rounded-full flex items-center justify-center transition-colors"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                                <input
                                                    type="text"
                                                    placeholder={t('itemName')}
                                                    value={item.itemName}
                                                    onChange={e => handleMenuChange(i, 'itemName', e.target.value)}
                                                    className="flex-1 bg-transparent text-white text-sm font-bold outline-none placeholder:text-slate-600 font-heebo min-w-0"
                                                />
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <span className="text-slate-500 text-xs">₪</span>
                                                    <input
                                                        type="number"
                                                        placeholder="0"
                                                        value={item.price}
                                                        onChange={e => handleMenuChange(i, 'price', e.target.value)}
                                                        className="w-14 bg-slate-900/50 border border-slate-700 rounded-lg px-2 py-1 text-xs text-purple-400 font-black outline-none text-center"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleItemExpand(i)}
                                                    className="flex-shrink-0 p-1 text-slate-600 hover:text-slate-300 transition-colors"
                                                    title="עריכה מורחבת / תוספות"
                                                >
                                                    {expandedItems[i] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                            </div>

                                            {/* Expanded: description + modifiers */}
                                            {expandedItems[i] && (
                                                <div className="px-3 pb-3 space-y-3 border-t border-slate-700/50 pt-3">
                                                    <input
                                                        type="text"
                                                        placeholder={t('descriptionOptional')}
                                                        value={item.description}
                                                        onChange={e => handleMenuChange(i, 'description', e.target.value)}
                                                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-300 outline-none font-heebo placeholder:text-slate-600"
                                                    />

                                                    {/* Modifiers */}
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-[10px] text-slate-500 font-black uppercase tracking-wider">{t('modifiersOptions')}</span>
                                                            <button type="button" onClick={() => addModifier(i)} className="text-[10px] text-sky-500 hover:underline">
                                                                {t('addGroup')}
                                                            </button>
                                                        </div>

                                                        {(item.modifiers || []).map((mod, mi) => (
                                                            <div key={mi} className="bg-slate-950/50 border border-slate-700/50 rounded-xl p-2.5 space-y-2">
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="text"
                                                                        placeholder={t('groupNamePlaceholder')}
                                                                        value={mod.name}
                                                                        onChange={e => updateModifier(i, mi, 'name', e.target.value)}
                                                                        className="flex-1 bg-transparent text-white text-[11px] font-bold outline-none placeholder:text-slate-600 font-heebo"
                                                                    />
                                                                    <button type="button" onClick={() => removeModifier(i, mi)} className="text-slate-600 hover:text-rose-500">
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </div>

                                                                <div className="flex gap-3 text-[10px] text-slate-500">
                                                                    <label className="flex items-center gap-1 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={mod.required}
                                                                            onChange={e => updateModifier(i, mi, 'required', e.target.checked)}
                                                                            className="accent-purple-500"
                                                                        />
                                                                        {t('required')}
                                                                    </label>
                                                                    <label className="flex items-center gap-1 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={mod.multiSelect}
                                                                            onChange={e => updateModifier(i, mi, 'multiSelect', e.target.checked)}
                                                                            className="accent-purple-500"
                                                                        />
                                                                        {t('multiSelect')}
                                                                    </label>
                                                                </div>

                                                                <div className="space-y-1.5">
                                                                    {mod.options.map((opt, oi) => (
                                                                        <div key={oi} className="flex items-center gap-2">
                                                                            <input
                                                                                type="text"
                                                                                placeholder={t('option')}
                                                                                value={opt.label}
                                                                                onChange={e => updateModifierOption(i, mi, oi, 'label', e.target.value)}
                                                                                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-white outline-none font-heebo"
                                                                            />
                                                                            <div className="flex items-center gap-0.5">
                                                                                <span className="text-slate-600 text-[10px]">+₪</span>
                                                                                <input
                                                                                    type="number"
                                                                                    placeholder="0"
                                                                                    value={opt.price}
                                                                                    onChange={e => updateModifierOption(i, mi, oi, 'price', e.target.value)}
                                                                                    className="w-12 bg-slate-800 border border-slate-700 rounded-lg px-1.5 py-1 text-[11px] text-purple-400 outline-none text-center"
                                                                                />
                                                                            </div>
                                                                            <button type="button" onClick={() => removeModifierOption(i, mi, oi)} className="text-slate-600 hover:text-rose-500">
                                                                                <X className="w-3 h-3" />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button type="button" onClick={() => addModifierOption(i, mi)} className="text-[10px] text-slate-600 hover:text-slate-400 font-heebo">
                                                                        {t('addOption')}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Sticky Save */}
                            <div className="px-5 py-4 border-t border-white/5 flex-shrink-0 bg-slate-900">
                                <button
                                    type="submit"
                                    className="w-full py-3 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-black rounded-xl transition-colors font-heebo"
                                >
                                    {t('saveRestaurant')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
